import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import { useMemo, useState } from "react";
import {
  GetProfileCareerQueryQuery,
  GetProfileCareerQueryQueryVariables,
} from "types/graphql-schema";

import { personCareerPath } from "@apps/org-settings/components/competencies/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { Select, SelectOption } from "@components/select/select";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";
import { titleCase } from "@helpers/string";

import ProfileTabEmptyState from "../components/profile-tab-empty-state";
import ProfileReviews from "../overview-tab/components/profile-reviews";
import ProfileCareerTable from "./components/career-table";
import getProfileCareerQuery from "./graphql/get-profile-career-query";

const ProfileCareerTab = ({ profileId }: { profileId: number }) => {
  const label = useLabel();
  const link = useLink();
  const queryParams = useUrlQueryParams<{
    careerTrackId: number | null;
  }>({
    careerTrackId: null,
  });
  const currentOrganization = currentOrganizationVar();
  const { data, loading } = useQuery<
    GetProfileCareerQueryQuery,
    GetProfileCareerQueryQueryVariables
  >(getProfileCareerQuery, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: { userId: profileId, organizationId: currentOrganization.id },
  });

  const profile = data?.user;
  const orgCareerTracksCount = data?.careerTracks.totalCount ?? 0;
  const roles = useMemo(() => profile?.currentRoles || [], [profile]);
  const tracks = useMemo(
    () =>
      uniqBy(
        roles.map((role) => role.careerTrack),
        "id",
      ).filter(Boolean),
    [roles],
  );
  const userRolesIds = useMemo(() => roles.map((role) => role.id), [roles]);
  const careerTrackOptions = useMemo(() => {
    return tracks.length > 0
      ? tracks.map((track) => ({
          label: track.title,
          value: track.id,
        }))
      : [{ label: "Select a career track...", value: 0 }];
  }, [tracks]);

  const suggestedTrackId = careerTrackOptions[0]?.value;
  const [_selectedCareerTrackId, setSelectedCareerTrackId] = useState<
    number | null
  >(queryParams.careerTrackId ? Number(queryParams.careerTrackId) : null);
  const selectedCareerTrackId = tracks.some(
    (track) => track.id === _selectedCareerTrackId,
  )
    ? _selectedCareerTrackId
    : suggestedTrackId;
  const selectedTrack = tracks.find(
    (track) => track.id === selectedCareerTrackId,
  );

  const handleCareerTrackChange = (option: SelectOption<number>) => {
    setSelectedCareerTrackId(option.value);
  };

  const isAdmin = isAdminVar();

  return (
    <div className="flex flex-col gap-12">
      <ProfileReviews profileId={profileId} bare />
      {loading ? (
        <Loading />
      ) : !profile ? (
        <div className="text-gray-500 text-sm text-center">
          No profile found.
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between pt-2">
            <div className="text-lg font-semibold text-gray-900">
              Career Tracks
            </div>
            {tracks.length > 0 && (
              <div className="flex gap-4 items-center text-gray-500">
                My Career Track(s)
                <Select<number>
                  options={careerTrackOptions}
                  value={selectedCareerTrackId ?? 0}
                  onChange={handleCareerTrackChange}
                />
                {/* This person's view in settings, where an admin can change
                    what's expected of them. */}
                {isAdmin && (
                  <Button
                    size={ButtonSize.medium}
                    text={`Open in ${label("career", { capitalize: true })} Tracks`}
                    aria-label="Profile career: Open in career tracks"
                    to={personCareerPath(currentOrganization.id, profileId)}
                  />
                )}
              </div>
            )}
          </div>
          {tracks.length === 0 ? (
            <div className="mt-6">
              <ProfileTabEmptyState
                title={
                  isAdmin && orgCareerTracksCount === 0
                    ? titleCase(
                        `No Tracks Setup In Your ${label("organization")}`,
                      )
                    : titleCase("No Tracks Setup")
                }
                body={
                  isAdmin
                    ? `Set up ${label("career")} roles and responsibilities to use this feature.`
                    : `Contact an administrator to get your ${label("career")} tracks set up.`
                }
                buttonText={
                  isAdmin
                    ? `Edit ${label("career", { capitalize: true })} Tracks`
                    : undefined
                }
                onButtonClick={
                  isAdmin
                    ? () =>
                        link.redirect(
                          `/settings/organization/${currentOrganization.id}/competencies`,
                        )
                    : undefined
                }
              />
            </div>
          ) : selectedTrack ? (
            <ProfileCareerTable
              key={selectedCareerTrackId}
              profile={profile}
              userRolesIds={userRolesIds}
              selectedCareerTrack={selectedTrack}
            />
          ) : (
            <div className="text-gray-500 text-sm text-center">
              No career track selected.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileCareerTab;
